import {
  Material,
  Mesh,
  MeshBasicMaterial,
  MeshNormalMaterial,
  MeshStandardMaterial,
  MeshToonMaterial,
  Object3D,
  Scene,
} from 'three'
import * as THREE from 'three'

// DISPOSE NODE FUNCTION
// What's worse, having your game hit 0fps because it keeps all the materials in memory, or having it run 30+ and being a little less 'optimized'?
// Well we're gonna just make it work by clearing memory from ThreeJS, which it doesn't do automatically
// WHY? Well because in larger applications we actually want to cache materials and such, they're expensive to load (apparently)
// But this function will DESTROY stuff forever, which serves the purpose for now

// Note: Execute not with disposeNode, but with disposeHierarchy
const disposeNode = (node: Object3D) => {
  if (node instanceof THREE.Mesh) {
    if (node.geometry) {
      node.geometry.dispose()
    }

    if (node.material) {
      if (Array.isArray(node.material)) {
        Object.values(node.material).forEach(
          (mtrl: MeshBasicMaterial & MeshStandardMaterial & MeshToonMaterial & MeshNormalMaterial) => {
            if (mtrl.map) mtrl.map.dispose()
            if (mtrl.lightMap) mtrl.lightMap.dispose()
            if (mtrl.bumpMap) mtrl.bumpMap.dispose()
            if (mtrl.normalMap) mtrl.normalMap.dispose()
            if (mtrl.specularMap) mtrl.specularMap.dispose()
            if (mtrl.envMap) mtrl.envMap.dispose()
            if (mtrl.alphaMap) mtrl.alphaMap.dispose()
            if (mtrl.aoMap) mtrl.aoMap.dispose()
            if (mtrl.displacementMap) mtrl.displacementMap.dispose()
            if (mtrl.emissiveMap) mtrl.emissiveMap.dispose()
            if (mtrl.gradientMap) mtrl.gradientMap.dispose()
            if (mtrl.metalnessMap) mtrl.metalnessMap.dispose()
            if (mtrl.roughnessMap) mtrl.roughnessMap.dispose()

            mtrl.dispose() // disposes any programs associated with the material
          },
        )
      } else {
        if (node.material.map) node.material.map.dispose()
        if (node.material.lightMap) node.material.lightMap.dispose()
        if (node.material.bumpMap) node.material.bumpMap.dispose()
        if (node.material.normalMap) node.material.normalMap.dispose()
        if (node.material.specularMap) node.material.specularMap.dispose()
        if (node.material.envMap) node.material.envMap.dispose()
        if (node.material.alphaMap) node.material.alphaMap.dispose()
        if (node.material.aoMap) node.material.aoMap.dispose()
        if (node.material.displacementMap) node.material.displacementMap.dispose()
        if (node.material.emissiveMap) node.material.emissiveMap.dispose()
        if (node.material.gradientMap) node.material.gradientMap.dispose()
        if (node.material.metalnessMap) node.material.metalnessMap.dispose()
        if (node.material.roughnessMap) node.material.roughnessMap.dispose()

        node.material.dispose() // disposes any programs associated with the material
      }
    }
  }
} // disposeNode
export const disposeHierarchy = (node: Object3D, callback?: (obj: Object3D) => {}) => {
  for (var i = node.children.length - 1; i >= 0; i--) {
    var child = node.children[i]
    disposeHierarchy(child, callback)
    callback && callback(child)
  }
  disposeNode(node)
}

export const disposeTree = (obj: Object3D) => {
  const dispose = (mesh: Mesh) => {
    if (!mesh.isMesh) return

    mesh.geometry.dispose()

    if (Array.isArray(mesh.material)) {
      // an array of materials
      for (const material of mesh.material) cleanMaterial(material)
    } else {
      cleanMaterial(mesh.material)
    }
  }
  obj.traverse(object => {
    dispose(object as Mesh)
  })
}

const cleanMaterial = (material: Material) => {
  // dispose material
  material.dispose()

  Object.values(material).forEach(value => {
    if (value && typeof value === 'object' && 'minFilter' in value) {
      // dispose textures
      value.dispose()
    }
  })
}

export const getObject3DByUUID = (parent: Object3D, uuid: string): Object3D | void => {
  parent.traverse(o => {
    if(o.uuid === uuid) {
      return o
    }
  })
}

export const getParentScene = (obj: Object3D) => {
  let parent = obj.parent
  while (parent?.parent && parent.type !== 'Scene') {
    parent = parent.parent
  }
  return parent as Scene
}
